import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { GoogleLogo, PasskeyIcon } from '@/components/shared/auth-icons'
import { HeaderImage } from '@/components/ui/icons'
import { isPasskeyAvailableHere, isPasskeyCancelled, supabase } from '@/lib/supabase'
import { useAuthPreferenceStore } from '@/store/useAuthPreferenceStore'

/** OAuth の戻り URL に付くエラーを取り出し、URL から消す */
const consumeAuthErrorFromUrl = (): string | null => {
  const url = new URL(window.location.href)
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
  const read = (key: string) => url.searchParams.get(key) ?? hashParams.get(key)

  const error = read('error')
  if (!error) return null

  const errorCode = read('error_code')
  const description = read('error_description') ?? ''

  for (const key of ['error', 'error_code', 'error_description']) {
    url.searchParams.delete(key)
    hashParams.delete(key)
  }
  url.hash = hashParams.toString()
  window.history.replaceState(window.history.state, '', url.toString())

  if (errorCode === 'signup_disabled' || /signups? not allowed/i.test(description)) {
    return 'このアカウントは招待されていません。管理者に連絡してください。'
  }
  // URL の文言は誰でも書き換えられるので画面には出さない（偽の案内を表示させられるのを防ぐ）
  console.warn('[SignIn] OAuth error:', error, errorCode, description)
  return 'サインインできませんでした。もう一度お試しください。'
}

/**
 * サインイン方法のボタン（Google とパスキーで同じ見た目にそろえる）
 * 配色は Google ブランドガイドラインのダークテーマ（Fill #131314 / Stroke #8E918F / Text #E3E3E3）
 */
const METHOD_BUTTON_CLASS =
  'flex h-12 w-full items-center justify-center gap-3 rounded-full border border-[#8E918F] bg-[#131314] px-6 text-[15px] font-medium text-[#E3E3E3] transition-colors hover:bg-[#1f1f21] active:bg-[#2a2a2c] disabled:opacity-60'

export const SignInPage = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [hintMessage, setHintMessage] = useState<string | null>(null)
  const [pending, setPending] = useState<'google' | 'passkey' | null>(null)
  const canUsePasskey = isPasskeyAvailableHere()
  const lastSignInMethod = useAuthPreferenceStore((state) => state.lastSignInMethod)
  const setLastSignInMethod = useAuthPreferenceStore((state) => state.setLastSignInMethod)

  useEffect(() => {
    setErrorMessage(consumeAuthErrorFromUrl())
  }, [])

  const handleGoogle = useCallback(async () => {
    setPending('google')
    setErrorMessage(null)
    // パスキー登録の案内（サインイン後）を出すかの判断に使う。パスキーを使っている端末の記録は上書きしない
    if (lastSignInMethod !== 'passkey') setLastSignInMethod('google')
    // 成功するとそのまま Google の画面へ遷移する。戻り先は今開いているページ
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.href },
    })
    if (error) {
      console.error('[SignIn] Google sign-in failed:', error)
      setErrorMessage('Google でのサインインを開始できませんでした')
      setPending(null)
    }
  }, [lastSignInMethod, setLastSignInMethod])

  const handlePasskey = useCallback(async () => {
    setPending('passkey')
    setErrorMessage(null)
    setHintMessage(null)
    // 成功時は onAuthStateChange 経由でアプリ本体に切り替わる
    const { error } = await supabase.auth.signInWithPasskey()
    setPending(null)
    if (!error) {
      setLastSignInMethod('passkey')
      return
    }
    // ブラウザは「パスキーがない」と「キャンセル」を区別して返さないので、どちらでも Google へ案内する
    if (isPasskeyCancelled(error)) {
      setHintMessage('パスキーが見つからない場合は、Google で続けてください。')
      return
    }
    console.error('[SignIn] Passkey sign-in failed:', error)
    toast.error('パスキーでサインインできませんでした。Google で続けてください。')
  }, [setLastSignInMethod])

  return (
    <div className="min-h-screen min-w-screen bg-linear-to-br from-gray-900 via-purple-900 to-gray-900 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md space-y-8 text-center">
        {/* タイトルとサービスの説明 */}
        <div className="space-y-2">
          <div className="h-30 sm:h-30 md:h-40 lg:h-40 w-full max-w-full overflow-hidden">
            <HeaderImage />
          </div>
          <p className="text-sm text-gray-300">招待制のプライベートサービスです</p>
        </div>

        <div className="mx-auto max-w-sm space-y-3">
          <button type="button" onClick={handleGoogle} disabled={pending !== null} className={METHOD_BUTTON_CLASS}>
            <GoogleLogo className="size-5 shrink-0" />
            Google で続ける
          </button>
          {canUsePasskey && (
            <button type="button" onClick={handlePasskey} disabled={pending !== null} className={METHOD_BUTTON_CLASS}>
              <PasskeyIcon className="size-5 shrink-0" />
              パスキーでサインイン
            </button>
          )}
        </div>

        <div className="mx-auto max-w-sm space-y-2 text-sm">
          {errorMessage && <p className="text-red-300">{errorMessage}</p>}
          {hintMessage && <p className="text-amber-200">{hintMessage}</p>}
          {canUsePasskey && !hintMessage && (
            <p className="text-gray-400">
              初めての方は Google で続けてください。
              <br />
              サインイン後にパスキーを登録できます。
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
